import { useEffect, useRef, useState, type ReactNode } from "react";
import { Settings2, X } from "lucide-react";
import { Button, IconButton } from "./button";
import { Panel } from "./Panel";
import { useDismissed } from "./dismiss";

/**
 * A screen's settings, behind a gear button that opens them in a panel anchored below it.
 *
 * Owned rather than built on a menu primitive, per the ruling that took the same decision
 * for the registry card's offers: the ui kit has neither a popover nor a dropdown, and one
 * anchored panel is less than the dependency would cost. It anchors its panel to itself and
 * leaves where it sits to whoever mounts it.
 *
 * Whether it is open is its own state and is never stored. What the panel shows is the
 * caller's, read from the settings on every render, so a change made elsewhere shows through
 * without this holding a copy that could disagree. Focus enters the panel when it opens: a
 * dialog left with focus on its trigger is one assistive technology never enters. A press
 * outside it or Escape closes it.
 */
export function SettingsPanel({
  title,
  note,
  closeLabel,
  children,
}: {
  /** The button's words, and the panel's name. */
  title: string;
  /** What the settings reach, beside the title: "Defaults for every figure". */
  note: string;
  /** The close button's name, as a reader hears it. */
  closeLabel: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const gearRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);
  useDismissed(open, panelRef, gearRef, () => {
    setOpen(false);
  });

  return (
    <div className="relative">
      <Button
        ref={gearRef}
        variant="secondary"
        size="dense"
        type="button"
        aria-label={title}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen(!open);
        }}
      >
        <Settings2 aria-hidden />
        {title}
      </Button>

      {open && (
        <Panel
          ref={panelRef}
          role="dialog"
          aria-label={title}
          className="absolute right-0 top-[calc(100%+6px)] z-20 w-[340px] max-w-[calc(100vw-32px)] text-left shadow-lg"
        >
          <div className="flex items-baseline gap-2">
            <span className="text-[13px] font-semibold">{title}</span>
            <span className="text-[11px] text-muted-foreground">{note}</span>
            <IconButton
              ref={closeRef}
              variant="quiet"
              size="dense"
              type="button"
              aria-label={closeLabel}
              onClick={() => {
                setOpen(false);
              }}
              className="ml-auto"
            >
              <X className="size-3.5" aria-hidden />
            </IconButton>
          </div>
          {children}
        </Panel>
      )}
    </div>
  );
}
